import * as Types from '@aeppic/types'
import { Logger, InMemoryLog } from '../model/log.js'
import { runAction, runActions } from '../dynamic/run-action.js'
import type { AeppicInterface } from './aeppic.js'
import type { CommandAeppicInterface } from './commands/executor.js'
import type { ActionResult, ActionOptions } from '../dynamic/run-action.js'
import { WriteLockedAeppic } from './write-locked-aeppic.js'

const MAX_ACTIONS_LOG_SIZE = 100

export interface ActionContext {
  [key: string]: any
}

export interface ActionConsole {
  log: (...args: any[]) => void
  info?: (...args: any[]) => void
  warn?: (...args: any[]) => void
  error?: (...args: any[]) => void
  debug?: (...args: any[]) => void
  trace?: (...args: any[]) => void
  dir?: (...args: any[]) => void
}

export type ActionPackageLoader = (packageRefs: Types.Reference[]) => Promise<Record<string, any>>

export interface ActionRunOptions {
  aeppic?: AeppicInterface | CommandAeppicInterface
  log?: Logger
  console?: ActionConsole
  globals?: { [key: string]: any }
  timeoutMs?: number
}

export interface ActionsOptions {
  log?: Logger
  packageLoader?: ActionPackageLoader
}

export class Actions {
  private _log: Logger
  private _inMemoryLog: InMemoryLog
  private _packageLoader: ActionPackageLoader | null

  constructor(private _aeppic: AeppicInterface | CommandAeppicInterface, options: ActionsOptions = {}) {
    if (options.log) {
      this._log = options.log.child({ scope: 'Action' })
    } else {
      this._inMemoryLog = new InMemoryLog({ size: MAX_ACTIONS_LOG_SIZE, defaults: { scope: 'Action' } })
      this._log = this._inMemoryLog.child({ source: 'Action', built: Date.now() })
    }

    this._packageLoader = options.packageLoader ?? null
  }

  // get RecentLog() {
  //   return this._log
  // }

  /** Execute one action without applying its writes; the caller owns the commit. */
  async runBuffered(actionIdentifier: Types.IdOrReference | Types.Document, context: ActionContext, options: ActionRunOptions = {}): Promise<ActionResult> {
    const writes = []
    const aeppic = new WriteLockedAeppic(options.aeppic ?? this._aeppic, { writes })
    const [result] = await this.run([actionIdentifier], context, { ...options, aeppic })
    if (!result) throw new Error('Action produced no result')
    return { ...result, writes: result.ok ? writes : [] }
  }

  async run(actionIdentifiers: (Types.IdOrReference|Types.Document)[], context: ActionContext, { aeppic, log, console: actionConsole, globals: actionGlobals, timeoutMs }: ActionRunOptions = {}): Promise<ActionResult[]> {
    const actions = await this._aeppic.getAll(actionIdentifiers)
    const actionLogger = log ?? this._log.child({ source: 'Action', started: Date.now() })
    const actionAeppic = aeppic ?? this._aeppic

    const baseGlobals: any = {
      ...actionGlobals,
      Aeppic: actionAeppic,
      context,
      log: actionLogger,
    }

    if (actionConsole) {
      baseGlobals.console = actionConsole
    }

    const hasPackageLoader = this._packageLoader != null
    const anyActionHasPackages = hasPackageLoader && actions.some(a => hasPackageRefs(a))

    if (!anyActionHasPackages) {
      return runActions(actions, baseGlobals, { Logger: actionLogger, timeoutMs })
    }

    const results: ActionResult[] = []
    let index = 0

    for (const action of actions) {
      if (!action) {
        throw new Error('Could not run unresolved action')
      }

      let globals = baseGlobals

      if (hasPackageRefs(action)) {
        try {
          const packages = await this._packageLoader(action.data.packages as Types.Reference[])
          globals = { ...baseGlobals, packages }
        } catch (error) {
          actionLogger.warn({ type: 'action:packages:error', error: error.message }, 'Failed to load packages for action %s', action.id)
        }
      }

      const result = await runAction(action, globals, { isolation: 'best', index, Logger: actionLogger, timeoutMs })
      results.push(result)
      index += 1

      if (!result.ok) {
        break
      }
    }

    return results
  }
}

function hasPackageRefs(action: Types.Document): boolean {
  const packages = action?.data?.packages
  return Array.isArray(packages) && packages.length > 0
}
